import { type EntityId, isCity } from '../../../sim';
import { formatAmounts } from '../../resources';
import { lockedText, tallyText, whereFrom } from '../text';
import type { GameEvent, LossTally } from '../types';
import type { DiffContext } from './context';

/**
 * Military units killed (or starved), one event each; the feed rolls them up
 * by area. A group with every member gone is wiped out (named on the first
 * of its units lost this time). Units that fell with a city are counted in
 * that city's own loss instead.
 */
export function unitsLost(c: DiffContext): GameEvent[] {
  if (c.cities.length === 0) return [];
  const { prev, next, playerId, rules } = c;
  // Own cities lost this tick: units in their territory are counted in the
  // city's own loss entry.
  const fallen = new Set<EntityId>();
  for (const [bid, b] of prev.buildings) {
    if (b.owner === playerId && isCity(b, rules) && !next.buildings.has(bid)) fallen.add(bid);
  }
  const groupsLeft = new Set<EntityId>();
  for (const u of next.units.values()) if (u.group) groupsLeft.add(u.group.id);
  const wiped = new Set<EntityId>();
  const events: GameEvent[] = [];
  for (const [id, u] of prev.units) {
    if (u.owner !== playerId || next.units.has(id) || !rules.units[u.type]?.military) continue;
    if (u.homeTerritory !== undefined && fallen.has(u.homeTerritory)) continue;
    const group = u.group && !groupsLeft.has(u.group.id) && !wiped.has(u.group.id) ? u.group : null;
    if (group) wiped.add(group.id);
    const tally: LossTally = {
      what: 'units',
      counts: { [c.unitName(u.type)]: 1 },
      ...(group ? { groups: [group.number] } : {}),
      ...(group && c.groupName?.(group.id)
        ? { groupNames: { [group.number]: c.groupName(group.id) as string } }
        : {}),
      place: whereFrom(u.pos, c.cities, c.cityLabel),
    };
    events.push({ kind: 'loss', text: tallyText(tally), pos: u.pos, tally });
  }
  return events;
}

/**
 * Own buildings gone since the last summary: a city (with the stock it held
 * and the units that fell with it) or a building holding stock, a completed
 * barracks (with what its city can no longer train), and anything else or a
 * site rolled up by area. A cancelled site isn't lost; nor is anything once
 * the player is out (elimination clears all).
 */
export function buildingsLost(c: DiffContext): GameEvent[] {
  const { prev, next, playerId, rules, cityLabel } = c;
  const events: GameEvent[] = [];
  for (const [id, b] of prev.buildings) {
    if (b.owner !== playerId || next.buildings.has(id)) continue;
    const reported =
      isCity(b, rules) || b.stock || (b.done && rules.buildings[b.type]?.capacity !== undefined);
    if (!reported) {
      if (c.cities.length === 0 || c.cancelled?.has(id)) continue;
      const name = b.done ? c.buildingName(b.type) : `${c.buildingName(b.type)} site`;
      const tally: LossTally = {
        what: 'buildings',
        counts: { [name]: 1 },
        place: whereFrom(b.pos, c.cities, cityLabel),
      };
      events.push({ kind: 'loss', text: tallyText(tally), pos: b.pos, tally });
      continue;
    }
    // A completed barracks: which units its city can no longer train.
    if (b.done && rules.buildings[b.type]?.capacity !== undefined) {
      const city =
        b.cityId && cityLabel
          ? `${cityLabel(b.cityId)}'s ${c.buildingName(b.type).toLowerCase()}${
              b.nowCityId ? ` (now ${cityLabel(b.nowCityId)}'s)` : ''
            }`
          : `Your ${c.buildingName(b.type)}`;
      const locked = lockedText(rules, b.type);
      events.push({
        kind: 'loss',
        text: `${city} destroyed${locked ? `: ${locked}` : ''}`,
        pos: b.pos,
        entityId: id,
      });
      continue;
    }
    // A city is reported even with nothing stored (razed before any haul).
    if (!b.stock && !isCity(b, rules)) continue;
    const lost = formatAmounts(b.stock ?? {});
    const name = (isCity(b, rules) && cityLabel?.(id)) || c.buildingName(b.type);
    // Own units gone since the last summary that stood in its territory (or
    // inside it): the ones that fell with it (spilled with nowhere to go,
    // killed once unsheltered, or in the fight that took it).
    let unitsLost = 0;
    for (const [unitId, u] of prev.units) {
      if (u.owner === playerId && u.homeTerritory === id && !next.units.has(unitId)) unitsLost++;
    }
    const units = unitsLost > 0 ? ` · ${unitsLost} ${unitsLost === 1 ? 'unit' : 'units'} lost` : '';
    events.push({
      kind: 'loss',
      text: `${lost ? `${name} lost with ${lost}` : `${name} lost`}${units}`,
      pos: b.pos,
      entityId: id,
    });
  }
  return events;
}
